import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getMailboxDelegations, updateMailboxDelegation } from '../../../../../crud/mailboxDelegations.crud'

export const MAILBOX_DELEGATION_ERROR_CODES = {
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  EXCHANGE_UNAVAILABLE: 'EXCHANGE_UNAVAILABLE',
  TRUSTEE_NO_MAILBOX: 'TRUSTEE_NO_MAILBOX',
}

const IDLE_STATE = {
  status: 'idle',
  delegations: null,
  errorCode: null,
}

function useMailboxDelegations({ customerId, employeeId, enabled }) {
  const [state, setState] = useState(IDLE_STATE)
  const [requestId, setRequestId] = useState(0)
  const [pending, setPending] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshErrorCode, setRefreshErrorCode] = useState(null)
  const hasDelegationsRef = useRef(false)
  const loadedForRef = useRef(null)
  const currentKey = enabled && customerId != null && employeeId != null ? `${customerId}:${employeeId}` : null
  const currentKeyRef = useRef(currentKey)
  currentKeyRef.current = currentKey

  const retry = useCallback(() => {
    setRefreshErrorCode(null)
    setRequestId((current) => current + 1)
  }, [])

  const applyChange = useCallback(
    async (change) => {
      const requestKey = currentKeyRef.current
      if (!requestKey) {
        return { ok: false, errorCode: MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE }
      }
      setPending(true)
      try {
        const response = await updateMailboxDelegation({ customerId, employeeId, ...change })
        if (currentKeyRef.current !== requestKey) {
          return { ok: true }
        }
        if (response?.data?.refreshErrorCode) {
          setRefreshing(true)
          setRequestId((current) => current + 1)
          return { ok: true }
        }
        hasDelegationsRef.current = response?.data != null
        setRefreshErrorCode(null)
        setState({ status: 'ready', delegations: response?.data ?? null, errorCode: null })
        return { ok: true }
      } catch (error) {
        if (currentKeyRef.current !== requestKey) {
          return { ok: true }
        }
        const errorCode = error?.response?.data?.errorCode ?? MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE
        return { ok: false, errorCode, message: error?.response?.data?.message }
      } finally {
        if (currentKeyRef.current === requestKey) {
          setPending(false)
        }
      }
    },
    [customerId, employeeId],
  )

  useEffect(() => {
    if (!enabled || customerId == null || employeeId == null) {
      hasDelegationsRef.current = false
      loadedForRef.current = null
      setPending(false)
      setRefreshing(false)
      setRefreshErrorCode(null)
      setState(IDLE_STATE)
      return undefined
    }

    const loadKey = `${customerId}:${employeeId}`
    const keepExisting = loadedForRef.current === loadKey && hasDelegationsRef.current
    loadedForRef.current = loadKey

    let cancelled = false
    if (keepExisting) {
      setRefreshing(true)
      setRefreshErrorCode(null)
    } else {
      hasDelegationsRef.current = false
      setPending(false)
      setRefreshing(false)
      setRefreshErrorCode(null)
      setState({ status: 'loading', delegations: null, errorCode: null })
    }

    getMailboxDelegations({ customerId, employeeId })
      .then((response) => {
        if (cancelled) {
          return
        }
        hasDelegationsRef.current = true
        setRefreshing(false)
        setRefreshErrorCode(null)
        setState({ status: 'ready', delegations: response?.data ?? null, errorCode: null })
      })
      .catch((error) => {
        if (cancelled) {
          return
        }
        setRefreshing(false)
        const errorCode = error?.response?.data?.errorCode ?? MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE
        if (hasDelegationsRef.current) {
          setRefreshErrorCode(errorCode)
          return
        }
        setState({ status: 'error', delegations: null, errorCode })
      })

    return () => {
      cancelled = true
    }
  }, [customerId, employeeId, enabled, requestId])

  return useMemo(
    () => ({
      status: state.status,
      delegations: state.delegations,
      errorCode: state.errorCode,
      pending,
      refreshing,
      refreshErrorCode,
      retry,
      applyChange,
    }),
    [state, pending, refreshing, refreshErrorCode, retry, applyChange],
  )
}

const MailboxDelegationsContext = React.createContext(null)

const providerPropTypes = {
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  enabled: PropTypes.bool,
  children: PropTypes.node,
}

export function MailboxDelegationsProvider({ customerId = null, employeeId = null, enabled = false, children = null }) {
  const value = useMailboxDelegations({ customerId, employeeId, enabled })
  return (
    <MailboxDelegationsContext.Provider value={enabled ? value : null}>{children}</MailboxDelegationsContext.Provider>
  )
}

MailboxDelegationsProvider.propTypes = providerPropTypes

export function useMailboxDelegationsState({ customerId, employeeId, enabled }) {
  const shared = useContext(MailboxDelegationsContext)
  const local = useMailboxDelegations({
    customerId,
    employeeId,
    enabled: shared ? false : enabled,
  })
  return shared ?? local
}

export default useMailboxDelegations
